/*
  The compose window sends either an email or a text. "Send As" lets the user pin
  the channel; on Default ('auto') it follows whoever is in To/CC.
*/

export type SendAsType = 'auto' | 'email' | 'sms';
export type SendChannelType = 'email' | 'sms';

export type RecipientOptionType = {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  isEmployee?: boolean;
  /* The customer contact this address belongs to; one contact can be listed under several. */
  contactId?: number | null;
  /* A customer contact's phone column for this number: 'Mobilephone' or 'Phone'. */
  phoneField?: string | null;
  /* The customer contact's Preferred Channel names this address. */
  isPreferred?: boolean;
  /* This is the customer contact's main email, not one of their additional addresses. */
  isPrimaryEmail?: boolean;
  /*
    Why this address cannot be sent to - "Not a valid email address: bob@gmailcom" - or null when it
    can. Listed so the sender sees what is on file, but never selectable; it carries neither email
    nor phone, so it reaches nobody (ContactSelectionBo.Unusable, story 15723).
  */
  unusable?: string | null;
  /*
    Unusable because nobody may text the number - it opted out, or a text found it to be a landline
    (story 15724) - not because it is malformed; unusable then says why, with the date.
  */
  isTextBlocked?: boolean;
};

export const SEND_AS_OPTIONS = [
  { id: 'auto', name: 'Default' },
  { id: 'email', name: 'Email' },
  { id: 'sms', name: 'SMS' },
];

/*
  The carrier's limits on one text, shared by the compose window and Batch Send so both
  stop a send before it is queued rather than failing at RingCentral after. Mirror
  SmsService.MaxRecipients / MaxTextLength, which the server enforces as well.

  Recipients: naming more than one makes a group message, which RingCentral caps at 10.
  Length: the text field is capped at 1000 characters (JS string length, like the server's).
*/
export const MAX_SMS_RECIPIENTS = 10;
export const MAX_SMS_TEXT_LENGTH = 1000;

/*
  A text may go out with no words when it carries something - a picture, or on SCE Reports a data
  table, sent as a picture of its rows - and with nothing attached it needs words. Words are the
  text with spaces and line breaks trimmed. The server applies the same rule before anything is
  queued (SmsService.HasNothingToSend), and refuses with the same sentence - SCE Reports' wording,
  and the Customers pages', which cannot attach a table, without one (CustomerMessageService).
*/
export const TEXT_NEEDS_CONTENT = 'A text needs a message, a picture or a table.';
export const TEXT_NEEDS_MESSAGE_OR_PICTURE = 'A text needs a message or a picture.';

export const hasNothingToSend = (text: string | null | undefined, attachmentCount: number) =>
  !(text ?? '').trim() && attachmentCount === 0;

/*
  The channels a contact can actually be reached on. Everything downstream - the
  badge, what is selectable, what sets the mode - reads this rather than looking
  at the underlying fields, so an employee with no phone# on file behaves as the
  email-only contact they are.
*/
export const channelsOf = (option: RecipientOptionType): SendChannelType[] =>
  [option?.email ? 'email' : null, option?.phone ? 'sms' : null].filter(
    Boolean
  ) as SendChannelType[];

/*
  The channel a contact commits the window to. A contact reachable both ways is
  ambiguous and settles nothing, which is why it returns null.
*/
export const channelOf = (option: RecipientOptionType): SendChannelType | null => {
  const channels = channelsOf(option);
  return channels.length === 1 ? channels[0] : null;
};

export const supportsChannel = (option: RecipientOptionType, channel: SendChannelType) =>
  channelsOf(option).includes(channel);

export const addressFor = (option: RecipientOptionType, channel: SendChannelType) =>
  (channel === 'email' ? option?.email : option?.phone) ?? '';

/*
  A recipient option's `id` is its ADDRESS, not a surrogate key - and for an employee it is always
  the email (see GrowerContactsService.ContactSelection). Shipping ids would send an employee's
  email address to the carrier on a text, which drops it silently.

  Resolve through addressFor instead, so every address matches the channel the message actually
  goes on. filter(Boolean) drops anyone unreachable on that channel - callers count what survived
  rather than sending to nobody.
*/
export const addressesFor = (list: RecipientOptionType[] | undefined, channel: SendChannelType) =>
  (list ?? []).map((option) => addressFor(option, channel)).filter(Boolean);

/*
  The first single-channel recipient sets the channel; To is considered before CC. Until one does,
  the window is on its autoChannel: email, except the Customers window, which opens on SMS (see
  CustomerComposeDialog).
*/
export const resolveChannel = (
  sendAs: SendAsType,
  to: RecipientOptionType[] = [],
  cc: RecipientOptionType[] = [],
  autoChannel: SendChannelType = 'email'
): SendChannelType => {
  if (sendAs === 'email' || sendAs === 'sms') return sendAs;

  const driver = [...(to ?? []), ...(cc ?? [])].map(channelOf).find(Boolean);

  return (driver as SendChannelType) ?? autoChannel;
};

/*
  Whether the channel is settled: either pinned outright, or decided by a
  single-channel contact already in To/CC. Until it is, Auto restricts nothing -
  both email and phone# contacts stay selectable, and the first one picked settles it.
*/
export const isChannelLocked = (
  sendAs: SendAsType,
  to: RecipientOptionType[] = [],
  cc: RecipientOptionType[] = []
) =>
  sendAs !== 'auto' ||
  [...(to ?? []), ...(cc ?? [])].some((option) => channelOf(option) !== null);

/*
  The pick list with each customer contact's addresses on the given channel moved to the top of that
  contact's group - so in Email mode their email comes before their numbers, and in SMS the reverse.
  Only the order inside a group changes: contacts, and everyone who is not a customer contact, stay
  where the server put them. The server lists a contact's addresses together (see
  SceRecipientService.ContactSelections), so a group is a run of options with one contactId.
*/
export const channelFirst = (list: RecipientOptionType[], channel: SendChannelType) => {
  let group = -1;

  return list
    .map((option, i) => {
      if (option.contactId == null || option.contactId !== list[i - 1]?.contactId) group += 1;
      return { option, group, rank: supportsChannel(option, channel) ? 0 : 1 };
    })
    .sort((a, b) => a.group - b.group || a.rank - b.rank)
    .map(({ option }) => option);
};

/* The badge on a list entry, showing which channels the contact can be reached on. */
export const channelTag = (option: RecipientOptionType) => {
  const channels = channelsOf(option);

  if (channels.length > 1) return { label: 'email + phone', color: 'default' as const };
  if (channels[0] === 'email') return { label: 'email', color: 'success' as const };
  if (channels[0] === 'sms') return { label: 'phone', color: 'info' as const };

  return { label: 'no contact info', color: 'default' as const };
};

/*
  The list shows the address for the active channel whenever the name alone cannot identify it.

  A single-channel contact carries its one address in its name already (see
  SceRecipientService.ContactSelections and GrowerContactsService.ContactSelection), so repeating it
  would read as a duplicate. An employee has two addresses and one name, so the label has to resolve
  the one actually in play.
*/
export const recipientLabel = (option: RecipientOptionType, channel: SendChannelType) => {
  if (!option) return '';
  if (!option.isEmployee && channelsOf(option).length <= 1) return option.name;

  const address = addressFor(option, channel);

  return address ? `${option.name} ${address}` : option.name;
};
